import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, ScrollText } from "lucide-react";

const NUMBER_LABELS = ["NZBN", "IRD No.", "ABN", "ACN", "EIN", "Company No.", "PAYE Ref", "USt-IdNr", "Steuernummer", "Betriebsnummer", "Other"];

const PRESETS = {
  NZ: { business_number_label: "NZBN", ot_daily_threshold_hours: 0, ot_weekly_threshold_hours: 0, flag_sunday: false, retention_years: 7 },
  AU: { business_number_label: "ABN", ot_daily_threshold_hours: 0, ot_weekly_threshold_hours: 38, flag_sunday: true, retention_years: 7 },
  US: { business_number_label: "EIN", ot_daily_threshold_hours: 0, ot_weekly_threshold_hours: 40, flag_sunday: false, retention_years: 3 },
  GB: { business_number_label: "PAYE Ref", ot_daily_threshold_hours: 0, ot_weekly_threshold_hours: 48, flag_sunday: false, retention_years: 6 },
  DE: { business_number_label: "Betriebsnummer", ot_daily_threshold_hours: 8, ot_weekly_threshold_hours: 0, flag_sunday: true, retention_years: 2 },
};

export function ComplianceSettingsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const { data } = useQuery({ queryKey: ["compliance"], queryFn: async () => (await api.get("/settings/compliance")).data });
  useEffect(() => { if (data && !form) setForm(data); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (body) => api.put("/settings/compliance", body),
    onSuccess: () => { toast.success(t("Timesheet record settings saved")); qc.invalidateQueries({ queryKey: ["compliance"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === "" ? 0 : Number(v));

  return (
    <Card className="rounded-md border p-4" data-testid="compliance-settings-card">
      <div className="flex items-center gap-2">
        <ScrollText className="h-4 w-4 text-muted-foreground" />
        <span className="label-caps text-muted-foreground">{t("Timesheet record requirements")}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("KlenaPro keeps the time record only — pay rates, earnings and deductions stay in your payroll system. Fill in the employer identity and working-time rules required where you operate; they are printed on every timesheet.")}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="compliance-presets">
        <span className="text-xs text-muted-foreground">{t("Quick preset")}:</span>
        {Object.keys(PRESETS).map((k) => (
          <Button key={k} type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setForm((f) => ({ ...f, ...PRESETS[k] }))} data-testid={`compliance-preset-${k}`}>{k}</Button>
        ))}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2">
          <Label>{t("Employer legal name")}</Label>
          <Input value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} placeholder="Quantum Lodge Ltd" data-testid="comp-legal-name" />
        </div>
        <div className="space-y-1">
          <Label>{t("Business number type")}</Label>
          <Select value={form.business_number_label || "NZBN"} onValueChange={(v) => set("business_number_label", v)}>
            <SelectTrigger className="h-9" data-testid="comp-number-label"><SelectValue /></SelectTrigger>
            <SelectContent>{NUMBER_LABELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t("Business number")}</Label>
          <Input value={form.business_number || ""} onChange={(e) => set("business_number", e.target.value)} data-testid="comp-business-number" />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label>{t("Employer address")}</Label>
          <Textarea rows={2} value={form.employer_address || ""} onChange={(e) => set("employer_address", e.target.value)} data-testid="comp-employer-address" />
        </div>
        <div className="space-y-1">
          <Label>{t("Daily overtime after (hours, 0 = off)")}</Label>
          <Input type="number" step="0.5" min="0" max="24" value={form.ot_daily_threshold_hours} onChange={(e) => set("ot_daily_threshold_hours", num(e.target.value))} data-testid="comp-ot-daily" />
          <p className="text-[11px] text-muted-foreground">{t("Germany: 8 h/day must be recorded explicitly.")}</p>
        </div>
        <div className="space-y-1">
          <Label>{t("Weekly overtime after (hours, 0 = off)")}</Label>
          <Input type="number" step="0.5" min="0" max="168" value={form.ot_weekly_threshold_hours} onChange={(e) => set("ot_weekly_threshold_hours", num(e.target.value))} data-testid="comp-ot-weekly" />
          <p className="text-[11px] text-muted-foreground">{t("USA 40 h · Australia 38 h · UK 48 h average limit.")}</p>
        </div>
        <label className="flex items-center justify-between gap-3 rounded-md border p-2.5 text-sm">
          <span>{t("Mark Sunday and public-holiday work on the timesheet")}</span>
          <Switch checked={!!form.flag_sunday} onCheckedChange={(v) => set("flag_sunday", v)} data-testid="comp-flag-sunday" />
        </label>
        <label className="flex items-center justify-between gap-3 rounded-md border p-2.5 text-sm">
          <span>{t("Print employee details (address, DOB, employment type) on the PDF")}</span>
          <Switch checked={form.show_employee_details !== false} onCheckedChange={(v) => set("show_employee_details", v)} data-testid="comp-show-employee" />
        </label>
        <div className="space-y-1">
          <Label>{t("Retention period (years)")}</Label>
          <Input type="number" min="1" max="15" value={form.retention_years} onChange={(e) => set("retention_years", num(e.target.value) || 7)} data-testid="comp-retention" />
          <p className="text-[11px] text-muted-foreground">{t("AU 7 · UK 6 · US 3 · DE 2 — records are never deleted by KlenaPro.")}</p>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={() => save.mutate(form)} disabled={save.isPending} className="gap-2" data-testid="comp-save-btn">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("Save")}
        </Button>
      </div>
    </Card>
  );
}
